"use client";

import { useEffect } from "react";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { clearAllCaches } from "@/lib/data/cache";
import { setSentryUser } from "@/lib/observability/sentry";
import { setPostHogUser } from "@/lib/observability/posthog";

/**
 * Drops the in-memory data-layer cache whenever the signed-in user changes.
 * The cache (src/lib/data/cache.ts) is shared across all callers and not
 * keyed by user_id, so without this hook user A's cached stats/marks would
 * leak into user B's view after a sign-out + sign-in in the same SPA session.
 *
 * Same-user events (INITIAL_SESSION, TOKEN_REFRESHED, USER_UPDATED, repeat
 * SIGNED_IN on reload) are no-ops — we only react to genuine identity
 * transitions including null → user, user → null, and user → other user.
 */
export function AuthCacheBoot() {
  useEffect(() => {
    const supabase = createSupabaseBrowserClient();
    let previousUserId: string | null | undefined = undefined;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUserId = session?.user?.id ?? null;
      setSentryUser(currentUserId);
      setPostHogUser(currentUserId);
      if (previousUserId === undefined) {
        previousUserId = currentUserId;
        return;
      }
      if (currentUserId !== previousUserId) {
        clearAllCaches();
        previousUserId = currentUserId;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return null;
}
